import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

import { useUser } from "../useUser";
import {
  Experiment,
  ExperimentEvaluationOptions,
  useStatsigClient,
} from "@statsig/react-bindings";
import { ActiveAdAtom } from "MVVM/State/ads";
import { IAllstarUser } from "MVVM/Views/Setup/Components/@types";
import { analytics } from "MVVM/Analytics";

interface UsePlaywireAdsParams {
  onComplete?: () => void;
  path?: string;
  onShowHiddenContainer?: () => void;
}

const checkExperiment = ({
  allstarUser,
  getExperiment,
}: {
  allstarUser: IAllstarUser;
  getExperiment: (
    name: string,
    options?: ExperimentEvaluationOptions,
  ) => Experiment;
}) => {
  const adApplicableUser =
    ["FREE", "INTERNAL"].includes(allstarUser?.user?.userGroup?.type) ||
    !allstarUser?.loggedIn;

  if (adApplicableUser) {
    const inAdsTestGroup = getExperiment("playwire_site_ads").get(
      "show_ads",
      false,
    );
    return inAdsTestGroup;
  } else {
    return false;
  }
};

export const usePlaywireAds = ({
  onComplete,
  path = "default",
  onShowHiddenContainer,
}: UsePlaywireAdsParams = {}) => {
  const { allstarUser } = useUser();
  const { getExperiment } = useStatsigClient();
  const [activeAd, setActiveAd] = useRecoilState(ActiveAdAtom);

  useEffect(() => {
    return () => {
      setActiveAd("");
    };
  }, [setActiveAd]);

  const triggerAd = useCallback(() => {
    const canShowAds = checkExperiment({ allstarUser, getExperiment });

    if (!window.ramp?.spaAds || !!activeAd || !canShowAds)
      return onShowHiddenContainer?.();

    const setupEventListeners = () => {
      if (window.Bolt) {
        window.Bolt.on(
          "precontent_ad_video",
          window.Bolt.BOLT_AD_STARTED,
          () => {
            analytics.track("Ad Started", {
              adType: "precontent_ad_video",
              adPath: path,
            });
            setActiveAd(path);
          },
        );
        window.Bolt.on(
          "precontent_ad_video",
          window.Bolt.BOLT_AD_COMPLETE,
          () => {
            onComplete?.();
            setActiveAd("");
          },
        );
        window.Bolt.on("precontent_ad_video", "showHiddenContainer", () => {
          setActiveAd("");
          onShowHiddenContainer?.();
        });
        window.Bolt.on("precontent_ad_video", window.Bolt.BOLT_AD_ERROR, () => {
          setActiveAd("");
          onShowHiddenContainer?.();
        });
      }
    };

    if (window.ramp) {
      if (window.Bolt) {
        setupEventListeners();
      } else {
        window.ramp.onPlayerReady = setupEventListeners;
      }
    }

    analytics.track("Ad Requested", {
      adType: "precontent_ad_video",
      adPath: path,
    });
    window.ramp!.spaAds({
      ads: {
        type: "precontent_ad_video",
      },
      countPageview: false,
      path: path,
    });
  }, [
    activeAd,
    path,
    onComplete,
    setActiveAd,
    allstarUser,
    getExperiment,
    onShowHiddenContainer,
  ]);

  return { triggerAd, activeAd };
};
